/**
 * auth/session — Firebase Authentication 이메일/비밀번호 세션 (G1, 2026-07-05).
 *
 * SDK 없이 REST(identitytoolkit)만 사용 — extraWorks.js(익명 REST) 선례와 동일 패턴.
 * 활성 조건: VITE_AUTH === 'firebase' && VITE_FIREBASE_API_KEY 설정.
 * 꺼져 있으면 전부 no-op(기존 X-API-Key 단독 동작 그대로).
 *
 * 토큰 수명: idToken 1시간 — 만료 5분 전 refreshToken 으로 자동 갱신.
 * 저장: localStorage 'iggg-auth-session' {email, idToken, refreshToken, expiresAt}.
 */

const STORAGE_KEY = "iggg-auth-session";
const FB_KEY = import.meta.env?.VITE_FIREBASE_API_KEY || "";
const VERIFICATION_COOLDOWN_MS = 60_000;

export const AUTH_ENABLED =
  (import.meta.env?.VITE_AUTH || "") === "firebase" && Boolean(FB_KEY);

// 구글 로그인: Firebase SDK signInWithPopup(동적 import) → 우리 localStorage 세션으로 브릿지.
// 승인 도메인(Firebase authorizedDomains)만 있으면 동작 — OAuth origin/클라ID env 불필요.
// 팝업은 iggg-schedule.firebaseapp.com 에서 열리므로 커스텀 도메인 추가설정이 없다.
const FB_AUTH_DOMAIN = "iggg-schedule.firebaseapp.com";
const FB_PROJECT_ID = "iggg-schedule";

let _sessionGeneration = 0;

function loadSession() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

function tokenClaims(idToken) {
  try {
    const part = String(idToken || "").split(".")[1] || "";
    const normalized = part.replaceAll("-", "+").replaceAll("_", "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
}

function sessionIdentity(session) {
  if (!session?.refreshToken) return "";
  const claims = tokenClaims(session.idToken);
  const subject = String(claims?.sub || claims?.user_id || "").trim();
  if (subject) return `uid:${subject}`;
  const email = String(claims?.email || session.email || "")
    .normalize("NFKC").trim().toLowerCase();
  return email ? `email:${email}` : `refresh:${session.refreshToken}`;
}

function sessionRefreshChain(session) {
  return String(session?.refreshToken || "");
}

function captureSessionGuard(session) {
  return {
    generation: _sessionGeneration,
    identity: sessionIdentity(session),
    refreshToken: sessionRefreshChain(session),
  };
}

function sessionGuardMatches(guard) {
  const current = loadSession();
  return Boolean(guard?.identity)
    && guard.generation === _sessionGeneration
    && guard.identity === sessionIdentity(current)
    && guard.refreshToken === sessionRefreshChain(current);
}

function currentSessionWithMetadata() {
  const current = loadSession();
  return current?.refreshToken
    ? withTokenMetadata(current, current.provider || "")
    : null;
}

function withTokenMetadata(session, fallbackProvider = "") {
  const claims = tokenClaims(session?.idToken);
  const firebaseClaims = claims?.firebase;
  const claimedProvider = (firebaseClaims && typeof firebaseClaims === "object")
    ? String(firebaseClaims.sign_in_provider || "")
    : "";
  const provider = claimedProvider || session?.provider || fallbackProvider;
  const hasVerifiedClaim = Object.prototype.hasOwnProperty.call(claims, "email_verified");
  // ID token이 있으면 claim이 명시적으로 true일 때만 확인 완료로 본다.
  // lookup 결과를 보존한 채 stale token을 갱신하는 동안 SSO가 먼저 열리는 것을 막는다.
  const emailVerified = session?.idToken
    ? hasVerifiedClaim && claims.email_verified === true
    : session?.emailVerified === true;
  return { ...session, provider, emailVerified };
}

function isSsoEligible(session) {
  if (!session?.refreshToken || !session.provider) return false;
  return session.emailVerified === true;
}

function verificationState(session) {
  if (!session?.refreshToken) {
    return { signedIn: false, provider: "", emailVerified: false, requiresVerification: false };
  }
  const provider = String(session.provider || "");
  const emailVerified = session.emailVerified === true;
  return {
    signedIn: true,
    provider,
    emailVerified,
    requiresVerification: provider === "password" ? !emailVerified : !provider && !emailVerified,
  };
}

// SMBM(머니앱) 세션 공유 — 같은 부모도메인(.igggstudio.com) 쿠키로 SSO.
// 견적앱 로그인 시 refreshToken 을 쿠키에 미러 → money.igggstudio.com(iframe·독립)이
// 읽어 자동 로그인. 백엔드(run.app)로는 안 감(도메인 불일치), JS 만 읽음.
const SSO_COOKIE = "iggg_sso";
function _writeSsoCookie(s) {
  try {
    if (typeof document === "undefined") return;
    const onIggg = location.hostname.endsWith("igggstudio.com");
    const dom = onIggg ? "; Domain=.igggstudio.com" : "";
    const sec = location.protocol === "https:" ? "; Secure" : "";
    if (s?.refreshToken) {
      const val = encodeURIComponent(JSON.stringify({
        email: s.email,
        refreshToken: s.refreshToken,
        provider: s.provider || "",
        emailVerified: s.emailVerified === true,
      }));
      document.cookie = `${SSO_COOKIE}=${val}; Path=/${dom}; Max-Age=${60 * 60 * 24 * 30}; SameSite=Lax${sec}`;
    } else {
      document.cookie = `${SSO_COOKIE}=; Path=/${dom}; Max-Age=0; SameSite=Lax${sec}`;
    }
  } catch { /* noop */ }
}

function saveSession(s, guard = null) {
  if (guard && !sessionGuardMatches(guard)) return false;
  _sessionGeneration += 1;
  if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  else localStorage.removeItem(STORAGE_KEY);
  _writeSsoCookie(isSsoEligible(s) ? s : null);
  window.dispatchEvent(new Event("iggg-auth-changed"));
  return true;
}

// 전역 로그아웃 진입점(?logout=1) — 위성 앱(허브 등)이 쿠키만 지우고 와도 아래
// 미러 블록이 localStorage 세션으로 쿠키를 재발급해 로그아웃이 무효가 되던 결함
// (전수감사 2026-07-28). 미러보다 먼저 세션·쿠키를 함께 비운다.
try {
  if (typeof location !== "undefined"
      && new URLSearchParams(location.search).get("logout") === "1") {
    localStorage.removeItem(STORAGE_KEY);
    _writeSsoCookie(null);
    history.replaceState(null, "", location.pathname);
  }
} catch { /* noop */ }

// 앱 로드 시 현재 세션을 SSO 쿠키에 즉시 미러(다음 refresh 를 기다리지 않게) — SMBM 자동 로그인.
// 역방향(사장 검출 2026-07-27: as. 에서 재로그인 요구): 이 도메인 localStorage 에
// 세션이 없어도 iggg_sso 쿠키가 있으면 복원 — www 로그인 한 번이면 as./my. 등
// ig works 의 다른 서브도메인 표면도 즉시 로그인된다. expiresAt=0 이라 첫 API
// 호출에서 refreshToken 교환(getIdToken 기존 흐름)으로 검증된다.
try {
  const _raw = loadSession();
  const _cur = _raw?.refreshToken ? withTokenMetadata(_raw, _raw.provider || "") : _raw;
  if (_cur?.refreshToken) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(_cur));
    _writeSsoCookie(isSsoEligible(_cur) ? _cur : null);
  } else if (typeof document !== "undefined") {
    const m = document.cookie.match(/(?:^|; )iggg_sso=([^;]*)/);
    if (m) {
      const d = JSON.parse(decodeURIComponent(m[1]) || "null");
      if (d?.refreshToken) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          email: d.email || "", refreshToken: d.refreshToken,
          idToken: "", expiresAt: 0, provider: d.provider || "",
          emailVerified: d.emailVerified === true,
        }));
        // metadata 없는 기존 cookie는 보호 화면에서 token을 갱신해 검증한다.
        // 공개 라우트 로드만으로 전역 SSO cookie를 삭제하지 않는다.
      }
    }
  }
} catch { /* noop */ }

export function currentEmail() {
  return loadSession()?.email || "";
}

export function isSignedIn() {
  return Boolean(loadSession()?.refreshToken);
}

export async function signIn(email, password) {
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${FB_KEY}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, returnSecureToken: true }),
    },
  );
  const data = await res.json();
  if (!res.ok) {
    const code = data?.error?.message || "SIGNIN_FAILED";
    const friendly = {
      EMAIL_NOT_FOUND: "이메일 또는 비밀번호가 올바르지 않습니다.",
      INVALID_PASSWORD: "이메일 또는 비밀번호가 올바르지 않습니다.",
      INVALID_LOGIN_CREDENTIALS: "이메일 또는 비밀번호가 올바르지 않습니다.",
      USER_DISABLED: "비활성화된 계정입니다. 관리자에게 문의하세요.",
      TOO_MANY_ATTEMPTS_TRY_LATER: "시도 횟수 초과 — 잠시 후 다시 시도하세요.",
    }[code] || "로그인에 실패했습니다. 잠시 후 다시 시도하세요.";
    throw new Error(friendly);
  }
  saveSession(withTokenMetadata({
    email: data.email,
    idToken: data.idToken,
    refreshToken: data.refreshToken,
    expiresAt: Date.now() + (Number(data.expiresIn) - 300) * 1000,
  }, "password"));
  return data.email;
}

/** 회원가입 — Firebase 계정 생성 + 로그인 상태. 승인은 별도(백엔드 /api/staff/register). */
export async function signUp(email, password) {
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${FB_KEY}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, returnSecureToken: true }),
    },
  );
  const data = await res.json();
  if (!res.ok) {
    const code = data?.error?.message || "SIGNUP_FAILED";
    const friendly = {
      EMAIL_EXISTS: "가입을 완료할 수 없습니다. 입력 정보를 확인한 뒤 다시 시도하세요.",
      WEAK_PASSWORD: "비밀번호는 6자 이상이어야 합니다.",
      INVALID_EMAIL: "이메일 형식이 올바르지 않습니다.",
    }[String(code).split(":")[0].trim()]
      || "가입을 완료할 수 없습니다. 입력 정보를 확인한 뒤 다시 시도하세요.";
    throw new Error(friendly);
  }
  saveSession(withTokenMetadata({
    email: data.email,
    idToken: data.idToken,
    refreshToken: data.refreshToken,
    expiresAt: Date.now() + (Number(data.expiresIn) - 300) * 1000,
  }, "password"));
  await sendEmailVerification();
  return data.email;
}

/** 구글 팝업 로그인 → 우리 localStorage 세션으로 브릿지. firebase SDK 는 동적 import(초기 번들 무증가). */
export async function signInWithGooglePopup() {
  const [{ initializeApp, getApps }, authMod] = await Promise.all([
    import("firebase/app"),
    import("firebase/auth"),
  ]);
  const { getAuth, GoogleAuthProvider, signInWithPopup, setPersistence, inMemoryPersistence } = authMod;
  const cfg = { apiKey: FB_KEY, authDomain: FB_AUTH_DOMAIN, projectId: FB_PROJECT_ID };
  const app = getApps().length ? getApps()[0] : initializeApp(cfg);
  const auth = getAuth(app);
  // 세션 정본은 우리 localStorage — SDK 자체 영속화는 끈다(이중 상태 방지).
  await setPersistence(auth, inMemoryPersistence);
  auth.languageCode = "ko";
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const result = await signInWithPopup(auth, provider);
  const user = result.user;
  const idToken = await user.getIdToken();
  saveSession({
    email: (user.email || "").toLowerCase(),
    idToken,
    refreshToken: user.refreshToken,
    expiresAt: Date.now() + 3300 * 1000,   // idToken 1h - 5분 여유
    provider: "google.com",
    emailVerified: user.emailVerified === true,
  });
  return user.email;
}

export function signOut() {
  saveSession(null);
}

async function refresh(session) {
  const guard = captureSessionGuard(session);
  const res = await fetch(
    `https://securetoken.googleapis.com/v1/token?key=${FB_KEY}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `grant_type=refresh_token&refresh_token=${encodeURIComponent(session.refreshToken)}`,
    },
  );
  const data = await res.json();
  if (!res.ok) {
    // 같은 요청 사이에 로그아웃하거나 다른 계정으로 전환했다면 현재 세션을 보존한다.
    const applied = saveSession(null, guard);
    return { session: applied ? null : currentSessionWithMetadata(), applied };
  }
  const next = withTokenMetadata({
    email: session.email,
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + (Number(data.expires_in) - 300) * 1000,
    provider: session.provider,
    emailVerified: session.emailVerified,
    verificationSentAt: session.verificationSentAt,
  }, session.provider);
  const applied = saveSession(next, guard);
  return { session: applied ? next : currentSessionWithMetadata(), applied };
}

let _refreshing = null;

/** 유효한 idToken 반환(필요 시 자동 갱신). 미로그인/실패 시 "". */
export async function getIdToken() {
  if (!AUTH_ENABLED) return "";
  let s = loadSession();
  if (!s?.refreshToken) return "";
  if (Date.now() < (s.expiresAt || 0) && s.idToken) return s.idToken;
  const refreshToken = sessionRefreshChain(s);
  let pending = _refreshing;
  if (!pending || pending.refreshToken !== refreshToken) {
    pending = { refreshToken, promise: null };
    pending.promise = refresh(s).finally(() => {
      if (_refreshing === pending) _refreshing = null;
    });
    _refreshing = pending;
  }
  const result = await pending.promise;
  if (!result.applied
      || sessionRefreshChain(loadSession()) !== sessionRefreshChain(result.session)) return "";
  return result.session?.idToken || "";
}

async function lookupAccount(session, { persist = true } = {}) {
  const initialGuard = captureSessionGuard(session);
  // 저장된 문자열이 있어도 만료됐을 수 있으므로 getIdToken이 expiresAt을 판정하게 한다.
  const idToken = await getIdToken();
  const resolvedSession = loadSession();
  let guard = initialGuard;
  if (!sessionGuardMatches(initialGuard)) {
    // getIdToken 내부 refresh가 정확히 한 번 세션을 갱신한 경우에만 회전된 chain을 이어받는다.
    const refreshedCurrentSession = Boolean(idToken)
      && _sessionGeneration === initialGuard.generation + 1
      && resolvedSession?.idToken === idToken;
    if (!refreshedCurrentSession) return currentSessionWithMetadata();
    guard = captureSessionGuard(resolvedSession);
  }
  if (!idToken) throw new Error("로그인 세션이 만료되었습니다. 다시 로그인해 주세요.");
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:lookup?key=${FB_KEY}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken }),
    },
  );
  const data = await res.json();
  if (!sessionGuardMatches(guard)) return currentSessionWithMetadata();
  if (!res.ok || !Array.isArray(data?.users) || !data.users[0]) {
    throw new Error("이메일 확인 상태를 불러오지 못했습니다. 잠시 후 다시 시도하세요.");
  }
  const user = data.users[0];
  const providerInfo = Array.isArray(user.providerUserInfo) ? user.providerUserInfo : [];
  const provider = String(
    resolvedSession?.provider
      || providerInfo[0]?.providerId
      || withTokenMetadata({ idToken }).provider
      || "",
  );
  const next = {
    ...resolvedSession,
    provider,
    emailVerified: user.emailVerified === true,
  };
  if (persist && !saveSession(next, guard)) return currentSessionWithMetadata();
  return next;
}

/** 현재 세션의 이메일 확인 상태. 오래된 세션만 Firebase lookup으로 보강한다. */
export async function getEmailVerificationState({ forceLookup = false, persistLookup = true } = {}) {
  const stored = loadSession();
  if (!stored?.refreshToken) return verificationState(null);
  let session = withTokenMetadata(stored, stored.provider || "");
  const hasKnownState = Boolean(session.provider)
    && typeof session.emailVerified === "boolean";
  if (forceLookup || !hasKnownState) {
    session = await lookupAccount(session, { persist: persistLookup });
  }
  else if (session.provider !== stored.provider
      || session.emailVerified !== stored.emailVerified) saveSession(session);
  return verificationState(session);
}

/** 링크 확인 후 Firebase user와 새 ID token claim을 모두 갱신한다. */
export async function refreshEmailVerification() {
  // lookup만으로 verified 상태를 저장하면 stale ID token을 가진 SSO 쿠키가 잠깐
  // 발급될 수 있다. 새 token claim 검증이 끝날 때까지 lookup 결과는 메모리에만 둔다.
  const lookedUp = await getEmailVerificationState({ forceLookup: true, persistLookup: false });
  if (!lookedUp.emailVerified) return lookedUp;
  const session = loadSession();
  const refreshed = session?.refreshToken ? await refresh(session) : null;
  return verificationState(refreshed?.session);
}

export function verificationCooldownRemaining(now = Date.now()) {
  const sentAt = Number(loadSession()?.verificationSentAt || 0);
  return Math.max(0, VERIFICATION_COOLDOWN_MS - (now - sentAt));
}

function verificationSendError(code) {
  const err = new Error(
    code === "TOO_MANY_ATTEMPTS_TRY_LATER"
      ? "요청이 너무 많습니다. 잠시 후 다시 시도하세요."
      : "인증 메일을 보내지 못했습니다. 잠시 후 다시 시도하세요.",
  );
  err.code = "VERIFICATION_SEND_FAILED";
  return err;
}

let _sendingVerification = null;

/** 현재 로그인한 계정에 Firebase 공식 VERIFY_EMAIL 메일을 보낸다. */
export function sendEmailVerification() {
  if (_sendingVerification) return _sendingVerification;
  const remaining = verificationCooldownRemaining();
  if (remaining > 0) {
    const err = new Error("인증 메일을 이미 보냈습니다. 잠시 후 다시 시도하세요.");
    err.code = "VERIFICATION_COOLDOWN";
    err.retryAfter = remaining;
    return Promise.reject(err);
  }
  _sendingVerification = (async () => {
    const idToken = await getIdToken();
    if (!idToken) throw new Error("로그인 세션이 만료되었습니다. 다시 로그인해 주세요.");
    const sessionAtStart = loadSession();
    const res = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=${FB_KEY}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestType: "VERIFY_EMAIL", idToken }),
      },
    );
    const data = await res.json();
    if (!res.ok) throw verificationSendError(data?.error?.message || "");
    const current = loadSession();
    if (current?.refreshToken === sessionAtStart?.refreshToken) {
      saveSession({ ...current, verificationSentAt: Date.now() });
    }
    return { sent: true };
  })().finally(() => { _sendingVerification = null; });
  return _sendingVerification;
}
